import React from 'react';
import { Text, View, type TextProps, type ViewProps, type ViewStyle } from 'react-native';
import { cn } from '@/utils/cn';
import { PressableScale, type PressableScaleProps } from './pressable-scale';

function isTextChild(children: React.ReactNode): children is string | number {
  return typeof children === 'string' || typeof children === 'number';
}

const cardClass = 'overflow-hidden rounded-2xl bg-card border border-neutral-900/5';

const cardStyle: ViewStyle = {
  borderCurve: 'continuous',
  boxShadow: '0 1px 2px rgba(23, 17, 10, 0.04), 0 6px 16px rgba(23, 17, 10, 0.06)',
};

export interface CardProps extends ViewProps {
  children: React.ReactNode;
  className?: string;
}

export function Card({ children, className, style, ...props }: CardProps) {
  return (
    <View className={cn(cardClass, className)} style={[cardStyle, style]} {...props}>
      {children}
    </View>
  );
}

export interface PressableCardProps extends PressableScaleProps {
  children: React.ReactNode;
  className?: string;
}

/** Card interativo: escala em mola ao toque + haptic leve no iOS. */
export function PressableCard({ children, className, style, ...props }: PressableCardProps) {
  return (
    <PressableScale
      haptic
      className={cn(cardClass, className)}
      style={[cardStyle, style as ViewStyle]}
      accessibilityRole="button"
      {...props}
    >
      {children}
    </PressableScale>
  );
}

export interface CardHeaderProps extends ViewProps {
  children: React.ReactNode;
  className?: string;
}

export function CardHeader({ children, className, ...props }: CardHeaderProps) {
  return (
    <View className={cn('flex flex-col gap-1.5 p-5', className)} {...props}>
      {children}
    </View>
  );
}

export interface CardTitleProps extends ViewProps {
  children: React.ReactNode;
  className?: string;
}

const cardTitleClass = 'font-semibold text-lg leading-snug tracking-tight text-neutral-900';

export function CardTitle({ children, className, ...props }: CardTitleProps) {
  if (isTextChild(children)) {
    return (
      <Text className={cn(cardTitleClass, className)} {...(props as TextProps)}>
        {children}
      </Text>
    );
  }

  return (
    <View className={cn(cardTitleClass, className)} {...props}>
      {children}
    </View>
  );
}

export interface CardDescriptionProps extends ViewProps {
  children: React.ReactNode;
  className?: string;
}

const cardDescriptionClass = 'text-sm leading-5 text-neutral-500';

export function CardDescription({ children, className, ...props }: CardDescriptionProps) {
  if (isTextChild(children)) {
    return (
      <Text className={cn(cardDescriptionClass, className)} {...(props as TextProps)}>
        {children}
      </Text>
    );
  }

  return (
    <View className={cn(cardDescriptionClass, className)} {...props}>
      {children}
    </View>
  );
}

export interface CardContentProps extends ViewProps {
  children: React.ReactNode;
  className?: string;
}

export function CardContent({ children, className, ...props }: CardContentProps) {
  return (
    <View className={cn('p-5 pt-0', className)} {...props}>
      {children}
    </View>
  );
}

export interface CardFooterProps extends ViewProps {
  children: React.ReactNode;
  className?: string;
}

export function CardFooter({ children, className, ...props }: CardFooterProps) {
  return (
    <View className={cn('flex flex-row items-center p-5 pt-0', className)} {...props}>
      {children}
    </View>
  );
}
